import Autocomplete from '@mui/material/Autocomplete';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import { useMemo, type FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

// ----------------------------------------------------------------------
// Timezone data

type TTimezoneOption = { value: string; label: string; offset: string };

const _getTimezoneOffset = (tz: string): string => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' }).formatToParts(
      new Date(),
    );
    return parts.find(p => p.type === 'timeZoneName')?.value || '';
  } catch {
    return '';
  }
};

const _parseOffsetToMinutes = (offset: string): number => {
  if (!offset || offset === 'GMT') {
    return 0;
  }
  const m = offset.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
  if (!m) {
    return 0;
  }
  return (m[1] === '+' ? 1 : -1) * (parseInt(m[2], 10) * 60 + parseInt(m[3] || '0', 10));
};

const TIMEZONE_VALUES = [
  'Pacific/Midway',
  'Pacific/Honolulu',
  'America/Anchorage',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Mexico_City',
  'America/Panama',
  'America/Bogota',
  'America/Lima',
  'America/Caracas',
  'America/La_Paz',
  'America/Santiago',
  'America/Halifax',
  'America/St_Johns',
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  'America/Noronha',
  'Atlantic/Azores',
  'Atlantic/Cape_Verde',
  'UTC',
  'Africa/Abidjan',
  'Africa/Casablanca',
  'Africa/Lagos',
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Africa/Nairobi',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Lisbon',
  'Europe/Paris',
  'Europe/Brussels',
  'Europe/Amsterdam',
  'Europe/Berlin',
  'Europe/Rome',
  'Europe/Madrid',
  'Europe/Zurich',
  'Europe/Stockholm',
  'Europe/Warsaw',
  'Europe/Helsinki',
  'Europe/Athens',
  'Europe/Bucharest',
  'Europe/Kyiv',
  'Europe/Istanbul',
  'Europe/Moscow',
  'Asia/Jerusalem',
  'Asia/Beirut',
  'Asia/Baghdad',
  'Asia/Tehran',
  'Asia/Qatar',
  'Asia/Riyadh',
  'Asia/Baku',
  'Asia/Muscat',
  'Asia/Dubai',
  'Asia/Kabul',
  'Asia/Karachi',
  'Asia/Tashkent',
  'Asia/Kolkata',
  'Asia/Colombo',
  'Asia/Kathmandu',
  'Asia/Dhaka',
  'Asia/Almaty',
  'Asia/Yangon',
  'Asia/Bangkok',
  'Asia/Jakarta',
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Taipei',
  'Asia/Manila',
  'Asia/Kuala_Lumpur',
  'Asia/Singapore',
  'Asia/Seoul',
  'Asia/Tokyo',
  'Australia/Perth',
  'Australia/Darwin',
  'Australia/Adelaide',
  'Australia/Brisbane',
  'Australia/Sydney',
  'Australia/Hobart',
  'Pacific/Noumea',
  'Pacific/Auckland',
  'Pacific/Fiji',
  'Pacific/Tongatapu',
  'Pacific/Apia',
];

const buildTimezoneOptions = (): TTimezoneOption[] =>
  TIMEZONE_VALUES.map(tz => {
    const offset = _getTimezoneOffset(tz);
    return { value: tz, label: `${tz.replace(/_/g, ' ')} (${offset})`, offset };
  }).sort((a, b) => _parseOffsetToMinutes(a.offset) - _parseOffsetToMinutes(b.offset));

// ----------------------------------------------------------------------

type TProps = Omit<TextFieldProps, 'name'> & {
  name: string;
};

export const RHFTimezone: FC<TProps> = ({ name, helperText, label, placeholder, ...rest }) => {
  const { control } = useFormContext();
  const options = useMemo(() => buildTimezoneOptions(), []);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Autocomplete
          options={options}
          value={options.find(o => o.value === field.value) ?? null}
          onChange={(_, option) => field.onChange(option?.value ?? '')}
          getOptionLabel={o => o.label}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          fullWidth
          renderInput={params => (
            <TextField
              {...params}
              {...rest}
              fullWidth
              label={label}
              placeholder={placeholder ?? 'Select timezone'}
              error={!!error}
              helperText={error ? error.message : helperText}
              sx={{ '& .MuiInputBase-root': { height: 60 } }}
            />
          )}
        />
      )}
    />
  );
};
